import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';

import { 
  getProductById, 
  ribbonColors, 
  finishOptions, 
  wrappingColors, 
  addOnOptions,
  flowerColorTones,
  realFlowerAddOns,
  valentineAddOns,
  boobouquetAddOns,
} from '../data/products';

const ProductDetail = () => {

  const formatAUD = (value) =>
    typeof value === 'number' ? `$${value.toFixed(2)}` : value;

  const getSalePrice = (price, promoPercent) => {
    if (typeof price !== 'number' || !promoPercent) return null;
    return Number((price * (1 - promoPercent / 100)).toFixed(2));
  };

  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [showColorError, setShowColorError] = useState(false);
  const [showGiftMessageError, setShowGiftMessageError] = useState(false);
  const [showColorPreferencesError, setShowColorPreferencesError] = useState(false);
  const [corsageType, setCorsageType] = useState(''); // 'corsage', 'boutonniere', or 'both'
  const [showAddedToCartModal, setShowAddedToCartModal] = useState(false);
  const product = getProductById(id);
  const [selectedImage, setSelectedImage] = useState(product?.images?.[0] || product?.image || '');
const [customization, setCustomization] = useState({
  color: '',
  finish: 'Normal',
});

  // Ribbon rose customization state
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedFinish, setSelectedFinish] = useState('Normal');
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [ferreroQuantity, setFerreroQuantity] = useState(0);
  const [wantCard, setWantCard] = useState(false);
  const [cardMessage, setCardMessage] = useState('');
  const [wantRibbonWriting, setWantRibbonWriting] = useState(false);
  const [ribbonMessage, setRibbonMessage] = useState('');

  // Real flower customization state
  const [selectedColorTones, setSelectedColorTones] = useState([]);
  const [customColorPreference, setCustomColorPreference] = useState('');
  const [selectedRealFlowerAddOns, setSelectedRealFlowerAddOns] = useState([]);
  const [giftMessage, setGiftMessage] = useState('');
  const [selectedRamoOption, setSelectedRamoOption] = useState(null);
  const [showRamoSelectionError, setShowRamoSelectionError] = useState(false);
  const [selectedBobaColor, setSelectedBobaColor] = useState('');
  const [showBobaColorError, setShowBobaColorError] = useState(false);

  const [selectedChocolate, setSelectedChocolate] = useState('');
  const [showChocolateError, setShowChocolateError] = useState(false);

  const [selectedBundle, setSelectedBundle] = useState(null);
  const [showBundleError, setShowBundleError] = useState(false);

  // Auto-select first ramo option by default
useEffect(() => {
  if (product?.isRamoStyle && product?.ramoOptions?.length > 0) {
    setSelectedRamoOption(0); // ✅ Default to first option
  }
}, [product]);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          {/* ✅ Mobile responsive */}
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">Product not found</h2>
          <button 
            onClick={() => navigate('/')}
            className="bg-rose-600 text-white px-4 md:px-6 py-2 md:py-3 rounded-lg hover:bg-rose-700 text-sm md:text-base"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Parse base price from product
  // Parse base price from product
const basePrice = typeof product.price === 'number' 
  ? product.price 
  : parseFloat(product.price.replace(/[^0-9.]/g, ''));  // ✅ Remove all non-numeric characters

    

  // Calculate add-ons total for ribbon roses
  const calculateAddOnsTotal = () => {
    let total = 0;
    selectedAddOns.forEach(addonId => {
      const addon = addOnOptions.find(a => a.id === addonId);
      if (addon) {
        if (addon.id === 'ferrero') {
          total += addon.price * ferreroQuantity;
        } else {
          total += addon.price;
        }
      }
    });
    return total;
  };

  const glitterPrice = selectedFinish === 'Glitter +$10' ? 10 : 0;


  // Add this near the top with other calculations
const getCorsagePrice = () => {
  if (product.category !== 'corsage') return basePrice;
  
  switch (corsageType) {
    case 'corsage':
      return 25;
    case 'boutonniere':
      return 25;
    case 'both':
      return 45;
    default:
      return 25;
  }
};

  // Calculate add-ons total for real flowers
  const calculateRealFlowerAddOnsTotal = () => {
  let total = 0;
  selectedRealFlowerAddOns.forEach(addonId => {
    // Check both realFlowerAddOns and product.addOns
    let addon = realFlowerAddOns.find(a => a.id === addonId);
    if (!addon && product.addOns) {
      addon = product.addOns.find(a => a.id === addonId);
    }
    if (addon) {
      total += addon.price;
    }
  });
  return total;
};

// Calculate discounted base price if there's a promo
const discountedBasePrice = product.promoPercent 
  ? getSalePrice(basePrice, product.promoPercent)
  : basePrice;

  // ✅ NEW: Calculate discounted ramo price
const ramoBase = product.isRamoStyle && selectedRamoOption !== null 
  ? product.ramoOptions[selectedRamoOption].price 
  : 0;

const ramoDiscounted = product.promoPercent && ramoBase > 0
  ? getSalePrice(ramoBase, product.promoPercent)
  : ramoBase;

  const totalPrice = product.category === 'corsage' 
  ? getCorsagePrice() + calculateAddOnsTotal() + glitterPrice
  : product.isRamoStyle && selectedRamoOption !== null
    ? ramoDiscounted + 
      (product.isCustomizable && product.category === 'valentine' 
        ? calculateAddOnsTotal() + calculateRealFlowerAddOnsTotal()  // ✅ Add BOTH for Valentine's ribbon roses
        : product.isCustomizable 
          ? calculateAddOnsTotal() 
          : calculateRealFlowerAddOnsTotal()) + 
      glitterPrice
    : (product.category === 'real' || product.category === 'valentine' || product.category === 'mothersday'
      ? discountedBasePrice + calculateRealFlowerAddOnsTotal()
      : discountedBasePrice + calculateAddOnsTotal() + glitterPrice);

  const toggleAddOn = (addonId) => {
    if (addonId === 'ferrero') {
      if (selectedAddOns.includes(addonId)) {
        setSelectedAddOns(selectedAddOns.filter(id => id !== addonId));
        setFerreroQuantity(0);
      } else {
        setSelectedAddOns([...selectedAddOns, addonId]);
        setFerreroQuantity(1);
      }
    } else {
      if (selectedAddOns.includes(addonId)) {
        setSelectedAddOns(selectedAddOns.filter(id => id !== addonId));
      } else {
        setSelectedAddOns([...selectedAddOns, addonId]);
      }
    }
  };

  const toggleRealFlowerAddOn = (addonId) => {
    if (selectedRealFlowerAddOns.includes(addonId)) {
      setSelectedRealFlowerAddOns(selectedRealFlowerAddOns.filter(id => id !== addonId));
    } else {
      setSelectedRealFlowerAddOns([...selectedRealFlowerAddOns, addonId]);
    }
  };

  const toggleColorTone = (tone) => {
    if (selectedColorTones.includes(tone)) {
      setSelectedColorTones(selectedColorTones.filter(t => t !== tone));
    } else {
      if (selectedColorTones.length < 1) {
        setSelectedColorTones([...selectedColorTones, tone]);
      }
    }
  };

  const validateRealFlowerCustomization = () => {
    const hasColorSelection = selectedColorTones.length > 0;
    const hasCustomColor = customColorPreference.trim() !== '';
    
    if (!hasColorSelection && !hasCustomColor) {
      setShowColorPreferencesError(true);
      return false;
    }
    
    return true;
  };

   // ✅ Determine which color to display based on product category
const displayColor = 
product.bobaColors?.length > 0
    ? selectedBobaColor
  : product.category === 'real'
    ? (() => {
        const parts = [];
        if (selectedColorTones.length > 0) {
          parts.push(selectedColorTones.join(' & '));
        }
        if (customColorPreference.trim()) {
          parts.push(customColorPreference.trim());
        }
        return parts.length > 0 ? parts.join(' + ') : null;
      })()
    : product.category === 'valentine' 
      ? selectedColor 
      : customization.color;

  const handleAddToCart = () => {

    if (product.isBundle && selectedBundle === null) {
  setShowBundleError(true);
  return;
}

    if (product.isRamoStyle && selectedRamoOption === null) {
  setShowRamoSelectionError(true);
  return;
  }

  // ✅ Validate boba balloon colour combo
if (product.bobaColors?.length > 0 && !selectedBobaColor) {
  setShowBobaColorError(true);
  return;
}

  // ✅ Validate color for Valentine's products
    if (product.category === 'valentine' && product.colors && product.colors.length > 0 && !selectedColor) {
      setShowColorError(true);
      return;
    }

    // ✅ Validate gift message for Valentine's products
    if (product.category === 'valentine' && product.giftMessage?.required && !giftMessage.trim()) {
      setShowGiftMessageError(true);
      return;
    }

    // ✅ Validate color for customizable ribbon roses
    if (product.isCustomizable && !customization.color) {
      setShowColorError(true);
      return;
    }



    // ✅ Validate chocolate choice for chocolate bouquets
if (product.hasChocolateChoice && !selectedChocolate) {
  setShowChocolateError(true);
  return;
}


    if (product.category === 'real') {
      if (!validateRealFlowerCustomization()) {
        return;
      }
      
      if (product.giftMessage?.required && !giftMessage.trim()) {
        setShowGiftMessageError(true);
        return;
      }
    }

    if (product.category === 'corsage' && !corsageType) {
    alert('Please select corsage type');
    return;
}

    const customProduct = {
  ...product,
  price: `$${totalPrice.toFixed(2)}`,
  customization: product.category === 'corsage' ? {
    type: corsageType,
    color: customization.color,
    finish: selectedFinish,
    addOns: selectedAddOns.map(id => {
      const addon = addOnOptions.find(a => a.id === id);
      if (id === 'ferrero') {
        return `${addon.name} x${ferreroQuantity}`;
      }
      return addon.name;
    }),
    cardMessage: wantCard ? cardMessage : null,
    ribbonMessage: wantRibbonWriting ? ribbonMessage : null,
  } : (product.isCustomizable ? {
    // ✅ CHECK CUSTOMIZABLE FIRST (before checking valentine)
    color: customization.color,
    finish: selectedFinish,
    roseCount: product.isRamoStyle && selectedRamoOption !== null 
      ? `${product.ramoOptions[selectedRamoOption].count} Roses` 
      : null,
    addOns: [
      // Regular ribbon rose add-ons
      ...selectedAddOns.map(id => {
        const addon = addOnOptions.find(a => a.id === id);
        if (id === 'ferrero') {
          return `${addon.name} x${ferreroQuantity}`;
        }
        return addon.name;
      }),
      // Valentine's add-ons (like polaroid) for Valentine's ribbon roses
      ...(product.category === 'valentine' ? selectedRealFlowerAddOns.map(id => {
        let addon = realFlowerAddOns.find(a => a.id === id);
        if (!addon && product.addOns) {
          addon = product.addOns.find(a => a.id === id);
        }
        return addon ? addon.name : '';
      }).filter(Boolean) : [])
    ],
    cardMessage: wantCard ? cardMessage : null,
    ribbonMessage: wantRibbonWriting ? ribbonMessage : null,
    giftMessage: product.giftMessage?.required ? giftMessage : null
  } : (product.category === 'real' || product.category === 'valentine' ? {
    // ✅ NOW this only catches non-customizable valentine products
    roseColor: product.category === 'valentine' ? selectedColor : null,
    bobaBalloonColor: product.bobaColors?.length > 0 ? selectedBobaColor : null,
    chocolateType: product.hasChocolateChoice ? selectedChocolate : null,
    colorTones: selectedColorTones,
    customColorPreference: customColorPreference,
    roseCount: product.isRamoStyle && selectedRamoOption !== null 
      ? `${product.ramoOptions[selectedRamoOption].count} Roses` 
      : null,
    addOns: selectedRealFlowerAddOns.map(id => {
      let addon = realFlowerAddOns.find(a => a.id === id);
      if (!addon && product.addOns) {
        addon = product.addOns.find(a => a.id === id);
      }
      return addon ? addon.name : '';
    }),
    giftMessage: giftMessage
  } : product.category === 'mothersday' ? {
    bundleSelection: product.isBundle ? product.bundleOptions[selectedBundle]?.label : null,
    addOns: selectedRealFlowerAddOns.map(id => {
      const addon = product.addOns?.find(a => a.id === id);
      return addon ? addon.name : '';
    }).filter(Boolean),
    giftMessage: giftMessage || null,
  } : product.isBundle ? {
    bundleSelection: product.bundleOptions[selectedBundle]?.label,
  } : null))
};

addToCart(customProduct, 1, displayColor);

// ✅ Show modal instead of navigating
  setShowAddedToCartModal(true);
  
  // ✅ Auto-close after 3 seconds
  setTimeout(() => {
    setShowAddedToCartModal(false);
  }, 4000);

  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-pink-50 to-white py-6 md:py-12">
      <div className="max-w-6xl mx-auto px-4 lg:px-6">

        
        {/* ✅ Back button - Mobile responsive */}
        <button 
          onClick={() => {
            navigate('/', {state: {scrollToProducts: true} });
          }}
          className="flex items-center gap-2 text-gray-600 hover:text-rose-600 mb-4 md:mb-8 transition text-sm md:text-base"
        >
          <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Products
        </button>

        {/* ✅ Grid - Stack on mobile, side-by-side on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          
          {/* Left side - Images */}
          <div className="md:col-span-6">
            <div className="md:sticky md:top-24">
              {/* ✅ Main image - Responsive height */}
              <div className="bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-lg md:shadow-xl mb-3 md:mb-4">
                <img
                  src={selectedImage}
                  alt={product.name}
                  className="w-full h-80 sm:h-96 md:h-[500px] lg:h-[600px] object-cover"
                />
              </div>

              {/* ✅ Thumbnail images - Smaller on mobile */}
              {product.images && product.images.length > 1 && (
                <div className="flex gap-2 md:gap-4 overflow-x-auto pb-2">
                  {product.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(image)}
                      className={`flex-shrink-0 w-16 h-16 md:w-24 md:h-24 rounded-lg overflow-hidden border-2 transition ${
                        selectedImage === image ? 'border-rose-600' : 'border-gray-200'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${product.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right side - Product info */}
          <div className="md:col-span-6">
            {/* ✅ Category badge - Smaller on mobile */}
            <span className="inline-block bg-rose-100 text-rose-700 px-3 md:px-4 py-1 rounded-full text-xs md:text-sm font-semibold mb-3 md:mb-4">
              {product.category === 'ribbon' ? 'Ribbon Rose' : 
               product.category === 'real' ? 'Fresh Flowers' :
               product.category === 'valentine' ? 'Valentine\'s Special' :
               product.category === 'corsage' ? 'Corsage & Boutonniere' :
               product.category === 'mothersday' ? 'Mother\'s Day Special' : 'BooBouquet'}
            </span>

            {/* ✅ Product name - Responsive size */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4 font-display">
              {product.name}
            </h1>

            {/* ✅ Price - Responsive size with discount support */}
{product.promoPercent ? (
  <div className="flex flex-col gap-2 mb-4 md:mb-6">
    <div className="flex items-center gap-3">
      <span className="text-sm md:text-base font-bold bg-rose-600 text-white px-3 py-1 rounded-full">
        {product.promoPercent}% OFF
      </span>
      <span className="text-xl md:text-2xl text-gray-400 line-through font-semibold">
        {formatAUD(basePrice)}
      </span>
    </div>
    <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-rose-600">
      ${totalPrice.toFixed(2)}
    </p>
  </div>
) : (
  <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-rose-600 mb-4 md:mb-6">
    ${totalPrice.toFixed(2)}
  </p>
)}

            {/* ✅ Description - Smaller text on mobile */}
            <p className="text-sm md:text-base lg:text-lg text-gray-700 mb-4 md:mb-6 lg:mb-8 leading-relaxed">
              {product.description}
            </p>

            {/* Full Description */}
            {product.fulldescription && (
              <p className="text-sm md:text-base text-gray-700 whitespace-pre-line leading-relaxed mb-4 md:mb-6">
                {product.fulldescription}
              </p>
            )}

            {/* ========== BUNDLE SELECTION ========== */}
{product.isBundle && product.bundleOptions && (
  <div className="bg-white border-2 border-rose-200 rounded-xl md:rounded-2xl p-4 md:p-6 mb-6">
    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">
      Choose Your Bundle:
    </h3>
    <div className="grid grid-cols-1 gap-3">
      {product.bundleOptions.map((option, index) => (
        <button
          key={index}
          type="button"
          onClick={() => {
            setSelectedBundle(index);
            setShowBundleError(false);
          }}
          className={`p-4 rounded-xl border-2 text-left transition-all ${
            selectedBundle === index
              ? 'border-rose-600 bg-rose-50'
              : 'border-gray-200 hover:border-rose-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-900 text-sm md:text-base">
              {option.label}
            </span>
            <span className="font-bold text-rose-600 text-sm md:text-base">
              ${option.price.toFixed(2)}
            </span>
          </div>
        </button>
      ))}
    </div>
    {selectedBundle === null && (
      <p className="text-xs md:text-sm text-gray-500 mt-3">
        Please select a bundle option before adding to cart
      </p>
    )}
  </div>
)}

            {/* ========== REAL FLOWER CUSTOMIZATION ========== */}
            {(product.category === 'real' || product.category === 'valentine' || product.category === 'mothersday') && (
              <div className="space-y-6 md:space-y-8">

                {/* Color Preferences */}
                {product.colorPreferences && (
                  <div className="bg-white border-2 border-rose-200 rounded-xl md:rounded-2xl p-4 md:p-6">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">
                      {product.colorPreferences.heading}
                    </h3>
                    <p className="text-gray-600 mb-3 md:mb-4 text-xs md:text-sm">
                      {product.colorPreferences.text}
                    </p>
                    
                    {flowerColorTones && (
                      <div className="grid grid-cols-1 gap-2 md:gap-3 mb-3 md:mb-4">
                        {flowerColorTones.map((tone, index) => (
                          <label
                            key={index}
                            className={`flex items-center p-2 md:p-3 border-2 rounded-lg md:rounded-xl cursor-pointer transition text-sm md:text-base ${
                              selectedColorTones.includes(tone)
                                ? 'border-rose-400 bg-rose-50'
                                : 'border-gray-200 hover:border-rose-300'
                            } ${selectedColorTones.length >= 2 && !selectedColorTones.includes(tone) ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedColorTones.includes(tone)}
                              onChange={() => toggleColorTone(tone)}
                              disabled={selectedColorTones.length >= 2 && !selectedColorTones.includes(tone)}
                              className="mr-2 md:mr-3 w-4 h-4 md:w-5 md:h-5 text-rose-600 rounded focus:ring-rose-500"
                            />
                            <span className="text-gray-700 font-medium">{tone}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {selectedColorTones.length > 0 && (
                      <p className="text-xs md:text-sm text-rose-600 mb-2 md:mb-3">
                        Selected: {selectedColorTones[0]}
                      </p>
                    )}
                    
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2 text-xs md:text-sm">
                        Or describe your color preferences:
                      </label>
                      <textarea
                        value={customColorPreference}
                        onChange={(e) => setCustomColorPreference(e.target.value)}
                        className="w-full px-3 md:px-4 py-2 md:py-3 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-rose-400 focus:outline-none resize-none text-sm md:text-base"
                        rows="3"
                        placeholder="e.g., Soft pinks and whites with touches of greenery..."
                      />
                    </div>
                  </div>
                )}

                {/* ✅ Boba Balloon Colour Combo Selection (Polaroid Holder) */}
{product.bobaColors?.length > 0 && (
  <div className="bg-white border-2 border-rose-200 rounded-xl md:rounded-2xl p-4 md:p-6 mb-6">
    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">
      Select Balloon Colour Combo:
    </h3>

    <div className="grid grid-cols-2 gap-3">
      {product.bobaColors.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => {
            setSelectedBobaColor(option);
            setShowBobaColorError(false);
          }}
          className={`p-3 rounded-xl border-2 text-sm font-semibold transition ${
            selectedBobaColor === option
              ? 'border-rose-600 bg-rose-50 text-rose-700'
              : 'border-gray-200 hover:border-rose-300 text-gray-700'
          }`}
        >
          {option}
        </button>
      ))}
    </div>

    {!selectedBobaColor && (
      <p className="text-xs md:text-sm text-gray-500 mt-3">
        Please select a colour combo before adding to cart
      </p>
    )}
  </div>
)}

             {/* ✅ Valentine Rose Colour Selection */}
{product.category === "valentine" && product.colors?.length > 0 && (
  <div className="bg-white border-2 border-rose-200 rounded-xl md:rounded-2xl p-4 md:p-6 mb-6">
    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">
      Select Rose Colour:
    </h3>

    <div className="flex gap-4">
      {product.colors.map((color) => (
        <button
          key={color}
          type="button"
          onClick={() => {
            setSelectedColor(color);
            setCustomization((prev) => ({ ...prev, color }));
          }} // ✅ important
          className={`rounded-lg border-2 transition-all ${
            selectedColor === color
              ? "border-black-600 ring-2 ring-rose-300"
              : "border-gray-200 hover:border-rose-300"
          }`}
          style={{
            background: 
            color === "Pink" ? "#F9A8D4" :
            color === "Red" ? "#DC2626" :
            color === "Pink and Red" ? "linear-gradient(90deg, #F9A8D4 50%, #DC2626 50%)" : 
              "#FFFFFF",
            width: color === "Pink and Red" ? "120px" : "48px",
            height: "48px"
          }}
        >
          {color === "Pink and Red" && (
            <span className="text-xs font-semibold text-white drop-shadow-md">
              Pink & Red
            </span>
          )}
        </button>
      ))}
    </div>

    {!selectedColor && (
      <p className="text-xs md:text-sm text-gray-500 mt-3">
        Please select a rose colour before adding to cart
      </p>
    )}
  </div>
)}

{/* ✅ Chocolate Choice Selection (for chocolate bouquets) */}
{product.hasChocolateChoice && product.chocolateOptions && (
  <div className="bg-white border-2 border-rose-200 rounded-xl md:rounded-2xl p-4 md:p-6 mb-6">
    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">
      Select Chocolate Type:
    </h3>

    <div className="grid grid-cols-2 gap-3">
      {product.chocolateOptions.map((chocolate) => (
        <button
          key={chocolate}
          type="button"
          onClick={() => {
            setSelectedChocolate(chocolate);
            setShowChocolateError(false);
          }}
          className={`p-3 rounded-xl border-2 text-sm font-semibold transition ${
            selectedChocolate === chocolate
              ? 'border-rose-600 bg-rose-50 text-rose-700'
              : 'border-gray-200 hover:border-rose-300 text-gray-700'
          }`}
        >
          {chocolate}
        </button>
      ))}
    </div>

    {!selectedChocolate && (
      <p className="text-xs md:text-sm text-gray-500 mt-3">
        Please select a chocolate type before adding to cart
      </p>
    )}
  </div>
)}



                {/* Ramo Style Rose Count Selection */}
{product.isRamoStyle && product.ramoOptions && (
  <div className="bg-white border-2 border-rose-200 rounded-xl md:rounded-2xl p-4 md:p-6 mb-6">
    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">
      Select Number of Roses:
    </h3>
    <select
      value={selectedRamoOption !== '' ? selectedRamoOption : ''}
      onChange={(e) => setSelectedRamoOption(e.target.value ? parseInt(e.target.value) : '')}
      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-rose-400 focus:outline-none text-base"
    >
      <option value="" disabled>Choose rose count...</option>
      {product.ramoOptions.map((option, index) => {
        const discountedPrice = product.promoPercent 
          ? getSalePrice(option.price, product.promoPercent)
          : option.price;
        
        return (
          <option key={index} value={index}>
            {option.count} Roses - {product.promoPercent 
              ? `Was $${option.price.toFixed(2)} → Now $${discountedPrice.toFixed(2)}` 
              : `$${option.price.toFixed(2)}`}
          </option>
        );
      })}
    </select>
  </div>
)}

                {/* Real Flower Add-Ons */}
                {product.category === 'real' && !product.isCustomOrder && realFlowerAddOns && realFlowerAddOns.length > 0 && (
                  <div className="bg-white border-2 border-rose-200 rounded-xl md:rounded-2xl p-4 md:p-6">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">
                      Optional Add-Ons
                    </h3>
                    <div className="space-y-2 md:space-y-3">
                      {realFlowerAddOns.map((addon) => (
                        <label
                          key={addon.id}
                          className={`flex items-center justify-between p-3 md:p-4 border-2 rounded-lg md:rounded-xl cursor-pointer transition text-sm md:text-base ${
                            selectedRealFlowerAddOns.includes(addon.id)
                              ? 'border-rose-400 bg-rose-50'
                              : 'border-gray-200 hover:border-rose-300'
                          }`}
                        >
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedRealFlowerAddOns.includes(addon.id)}
                              onChange={() => toggleRealFlowerAddOn(addon.id)}
                              className="mr-3 md:mr-4 w-4 h-4 md:w-5 md:h-5 text-rose-600 rounded focus:ring-rose-500"
                            />
                            <span className="text-gray-800 font-medium">{addon.name}</span>
                          </div>
                          <span className="text-rose-600 font-bold">
                            ${addon.price}
                          </span>
                        </label>
                      ))}
                    </div>
                    {selectedRealFlowerAddOns.length > 0 && (
                      <p className="text-xs md:text-sm text-gray-500 mt-2 md:mt-3">
                        Add-ons total: ${calculateRealFlowerAddOnsTotal().toFixed(2)}
                      </p>
                    )}
                  </div>
                )}

                {/* Valentine's Add-Ons - FOR VALENTINE'S PRODUCTS */}
{product.category === 'valentine' && product.addOns && product.addOns.length > 0 && (
  <div className="bg-white border-2 border-rose-200 rounded-xl md:rounded-2xl p-4 md:p-6">
    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">
      ✨ Optional Add-Ons
    </h3>
    <div className="space-y-2 md:space-y-3">
      {product.addOns.map((addon) => (
        <label
          key={addon.id}
          className={`flex items-center justify-between p-3 md:p-4 border-2 rounded-lg md:rounded-xl cursor-pointer transition text-sm md:text-base ${
            selectedRealFlowerAddOns.includes(addon.id)
              ? 'border-rose-400 bg-rose-50'
              : 'border-gray-200 hover:border-rose-300'
          }`}
        >
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={selectedRealFlowerAddOns.includes(addon.id)}
              onChange={() => toggleRealFlowerAddOn(addon.id)}
              className="mr-3 md:mr-4 w-4 h-4 md:w-5 md:h-5 text-rose-600 rounded focus:ring-rose-500"
            />
            <span className="text-gray-800 font-medium">{addon.name}</span>
          </div>
          <span className="text-rose-600 font-bold">
            ${addon.price}
          </span>
        </label>
      ))}
    </div>
    {selectedRealFlowerAddOns.length > 0 && (
      <p className="text-xs md:text-sm text-gray-500 mt-2 md:mt-3">
        Add-ons total: ${calculateRealFlowerAddOnsTotal().toFixed(2)}
      </p>
    )}
  </div>
)}

{/* Mother's Day Add-Ons */}
{product.category === 'mothersday' && product.addOns && product.addOns.length > 0 && (
  <div className="bg-white border-2 border-rose-200 rounded-xl md:rounded-2xl p-4 md:p-6">
    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">
      Optional Add-Ons
    </h3>
    <div className="space-y-2 md:space-y-3">
      {product.addOns.map((addon) => (
        <div key={addon.id}>
          <label
            className={`flex items-center justify-between p-3 md:p-4 border-2 rounded-lg md:rounded-xl cursor-pointer transition text-sm md:text-base ${
              selectedRealFlowerAddOns.includes(addon.id)
                ? 'border-rose-400 bg-rose-50'
                : 'border-gray-200 hover:border-rose-300'
            }`}
          >
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectedRealFlowerAddOns.includes(addon.id)}
                onChange={() => toggleRealFlowerAddOn(addon.id)}
                className="mr-3 md:mr-4 w-4 h-4 md:w-5 md:h-5 text-rose-600 rounded focus:ring-rose-500"
              />
              <span className="text-gray-800 font-medium">{addon.name}</span>
            </div>
            <span className="text-rose-600 font-bold">+${addon.price}</span>
          </label>

          {addon.id === 'mday-card' && selectedRealFlowerAddOns.includes('mday-card') && (
            <textarea
              value={giftMessage}
              onChange={(e) => setGiftMessage(e.target.value)}
              placeholder="Write your card message here..."
              rows={3}
              className="w-full mt-2 px-3 md:px-4 py-2 md:py-3 border-2 border-rose-200 rounded-lg focus:border-rose-400 focus:outline-none resize-none text-sm md:text-base"
            />
          )}
        </div>
      ))}
    </div>
  </div>
)}

                {/* Gift Message */}
                {product.giftMessage && (
                  <div className="bg-rose-50 border-2 border-rose-200 rounded-xl md:rounded-2xl p-4 md:p-6">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    Gift Message
                      {product.giftMessage.required && (
                        <span className="text-rose-600 text-xs md:text-sm">(Required)</span>
                      )}
                    </h3>
                    <p className="text-gray-600 mb-3 md:mb-4 text-xs md:text-sm">
                      {product.giftMessage.text}
                    </p>
                    <textarea
                      value={giftMessage}
                      onChange={(e) => setGiftMessage(e.target.value)}
                      required={product.giftMessage.required}
                      className="w-full px-3 md:px-4 py-2 md:py-3 border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-rose-400 focus:outline-none resize-none text-sm md:text-base"
                      rows="4"
                      placeholder="Write a heartfelt message for your recipient..."
                    />
                  </div>
                )}

              </div>
            )}

            {/* ========== RIBBON ROSE CUSTOMIZATION ========== */}
            {product.isCustomizable && (
              <div className="space-y-6 md:space-y-8">

                {/* Color Selection */}
                {product.category !== 'valentine' && product.colors && product.colors.length > 0 && (
  <div>
    <label className="block text-gray-800 font-semibold mb-3 text-lg">
      Choose Colour:
    </label>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {product.colors.map((color) => {
        // Handle both string and object formats
        const colorName = typeof color === 'string' ? color : color.name;
        const colorHex = typeof color === 'object' ? color.hex : null;
        
        return (
          <button
            key={colorName}
            onClick={() => setCustomization({ ...customization, color: colorName })}
            className={`px-4 py-3 border-2 transition-all duration-300 flex items-center gap-2 ${
              customization.color === colorName
                ? 'border-rose-600 bg-rose-50 text-rose-700 font-semibold'
                : 'border-gray-200 hover:border-rose-300 text-gray-700'
            }`}
          >
            {/* Color Preview Circle */}
            {colorHex && (
              <div 
                className="w-6 h-6 rounded-full border-2 border-gray-300 shadow-sm flex-shrink-0"
                style={{ backgroundColor: colorHex }}
              />
            )}
            <span className="text-sm flex-1">{colorName}</span>
          </button>
        );
      })}
    </div>
  </div>
)}

                {/* Corsage Type Selection - ONLY for corsages */}
{product.category === 'corsage' && (
  <div>
    <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3 md:mb-4">
      Choose Type: <span className="text-rose-600"></span>
    </h3>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
      <button
        onClick={() => setCorsageType('corsage')}
        className={`p-4 md:p-6 rounded-xl border-2 transition-all text-center ${
          corsageType === 'corsage'
            ? 'border-rose-600 bg-rose-50'
            : 'border-gray-200 hover:border-rose-300'
        }`}
      >
        <div className="font-bold text-gray-900">Corsage Only</div>
        <div className="text-rose-600 font-bold mt-2">$25.00</div>
      </button>

      <button
        onClick={() => setCorsageType('boutonniere')}
        className={`p-4 md:p-6 rounded-xl border-2 transition-all text-center ${
          corsageType === 'boutonniere'
            ? 'border-rose-600 bg-rose-50'
            : 'border-gray-200 hover:border-rose-300'
        }`}
      >
        <div className="font-bold text-gray-900">Boutonniere Only</div>
        <div className="text-rose-600 font-bold mt-2">$25.00</div>
      </button>

      <button
        onClick={() => setCorsageType('both')}
        className={`p-4 md:p-6 rounded-xl border-2 transition-all text-center ${
          corsageType === 'both'
            ? 'border-rose-600 bg-rose-50'
            : 'border-gray-200 hover:border-rose-300'
        }`}
      >
        <div className="font-bold text-gray-900">Corsage + Boutonniere</div>
        <div className="text-rose-600 font-bold mt-2">$45.00</div>
      </button>
    </div>
  </div>
)}

                {!product.isBooBouquet && (
                  <>
                    {/* Finish Selection */}
                    <div>
                      <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3 md:mb-4">Finish:</h3>
                      <div className="flex gap-2 md:gap-3">
                        {finishOptions.map((finish) => (
                          <button
                            key={finish}
                            onClick={() => setSelectedFinish(finish)}
                            className={`px-4 md:px-6 py-2 md:py-3 rounded-lg border-2 transition flex-1 text-sm md:text-base ${
                              selectedFinish === finish
                                ? 'border-rose-600 bg-rose-50 text-rose-700'
                                : 'border-gray-200 hover:border-rose-300'
                            }`}
                          >
                            {finish}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Add-ons */}
                    <div>
                      <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3 md:mb-4">Add-ons (Optional):</h3>
                      <div className="space-y-2 max-h-60 overflow-y-auto p-2 border rounded-lg">
                        {addOnOptions.map((addon) => (
                          <div key={addon.id}>
                            <label className="flex items-center justify-between p-2 md:p-3 rounded-lg hover:bg-gray-50 cursor-pointer">
                              <div className="flex items-center gap-2 md:gap-3">
                                <input
                                  type="checkbox"
                                  checked={selectedAddOns.includes(addon.id)}
                                  onChange={() => toggleAddOn(addon.id)}
                                  className="w-4 h-4 md:w-5 md:h-5 text-rose-600 rounded focus:ring-rose-500"
                                />
                                <span className="text-gray-700 text-sm md:text-base">{addon.name}</span>
                              </div>
                              <span className="font-semibold text-gray-900 text-sm md:text-base">+${addon.price}</span>
                            </label>
                            
                            {addon.id === 'ferrero' && selectedAddOns.includes('ferrero') && (
                              <div className="ml-6 md:ml-8 mt-2 flex items-center gap-2 md:gap-3">
                                <span className="text-xs md:text-sm text-gray-600">Quantity:</span>
                                <button
                                  onClick={() => setFerreroQuantity(Math.max(1, ferreroQuantity - 1))}
                                  className="w-7 h-7 md:w-8 md:h-8 rounded border-2 border-gray-200 hover:border-rose-600 flex items-center justify-center text-sm md:text-base"
                                >
                                  −
                                </button>
                                <span className="w-6 md:w-8 text-center font-semibold text-sm md:text-base">{ferreroQuantity}</span>
                                <button
                                  onClick={() => setFerreroQuantity(ferreroQuantity + 1)}
                                  className="w-7 h-7 md:w-8 md:h-8 rounded border-2 border-gray-200 hover:border-rose-600 flex items-center justify-center text-sm md:text-base"
                                >
                                  +
                                </button>
                                <span className="text-xs md:text-sm text-gray-500">
                                  (${addon.price * ferreroQuantity})
                                </span>
                              </div>
                            )}

                          </div>
                        ))}
                      </div>
                      {selectedAddOns.length > 0 && (
                        <p className="text-xs md:text-sm text-gray-500 mt-2">
                          Add-ons total: ${calculateAddOnsTotal().toFixed(2)}
                        </p>
                      )}
                    </div>

                    {/* Message Card (only show if NOT required) */}
{!product.giftMessage?.required && (
  <div>
    <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
      <input
        type="checkbox"
        id="wantCard"
        checked={wantCard}
        onChange={(e) => setWantCard(e.target.checked)}
        className="w-4 h-4 md:w-5 md:h-5 text-rose-600 rounded focus:ring-rose-500"
      />
      <label htmlFor="wantCard" className="text-base md:text-xl font-semibold text-gray-900 cursor-pointer">
        Add a message card? (Free)
      </label>
    </div>

    {wantCard && (
      <textarea
        value={cardMessage}
        onChange={(e) => setCardMessage(e.target.value)}
        placeholder="Enter your message here..."
        className="w-full px-3 md:px-4 py-2 md:py-3 border-2 border-gray-200 rounded-lg focus:border-rose-600 focus:outline-none text-sm md:text-base"
        rows="3"
      />
    )}
  </div>
)}

                    {/* Ribbon Writing */}
                    <div>
  <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
    <input
      type="checkbox"
      id="wantRibbon"
      checked={wantRibbonWriting}
      onChange={(e) => {
        setWantRibbonWriting(e.target.checked);
        // ✅ Add/remove from selectedAddOns
        if (e.target.checked) {
          setSelectedAddOns([...selectedAddOns, 'ribbon-writing']);
        } else {
          setSelectedAddOns(selectedAddOns.filter(id => id !== 'ribbon-writing'));
        }
      }}
      className="w-4 h-4 md:w-5 md:h-5 text-rose-600 rounded focus:ring-rose-500"
    />
    <label htmlFor="wantRibbon" className="text-base md:text-xl font-semibold text-gray-900 cursor-pointer">
      Add ribbon writing? <span className="text-rose-600">(+$5)</span>
    </label>
  </div>
  {wantRibbonWriting && (
    <input
      type="text"
      value={ribbonMessage}
      onChange={(e) => setRibbonMessage(e.target.value)}
      placeholder="Enter ribbon message (keep it short)..."
      className="w-full px-3 md:px-4 py-2 md:py-3 border-2 border-gray-200 rounded-lg focus:border-rose-600 focus:outline-none text-sm md:text-base"
    />
  )}
</div>
                  </>
                )}

              </div>
            )}

            {/* ✅ Add to cart button - Responsive */}
            {product.isCustomOrder ? (
  <button
    onClick={() => navigate('/contact')}
    className="w-full bg-rose-600 hover:bg-rose-700 text-white py-3 md:py-4 lg:py-5 rounded-lg md:rounded-xl text-base md:text-lg lg:text-xl font-bold transition-all duration-300 hover:scale-105 shadow-lg mt-6 md:mt-8 mb-3 md:mb-4"
  >
    Contact Us to Order
  </button>
) : product.soldOut ? (
  // ✅ SOLD OUT STATE
  <div className="mt-6 md:mt-8 mb-3 md:mb-4">
    <div className="w-full bg-gray-400 text-white py-3 md:py-4 lg:py-5 rounded-lg md:rounded-xl text-base md:text-lg lg:text-xl font-bold text-center cursor-not-allowed">
      SOLD OUT
    </div>
    <p className="text-center text-gray-600 text-sm mt-2">
      This item is currently unavailable
    </p>
  </div>
) : (
  <button
    onClick={handleAddToCart}
    className="w-full bg-rose-600 hover:bg-rose-700 text-white py-3 md:py-4 lg:py-5 rounded-lg md:rounded-xl text-base md:text-lg lg:text-xl font-bold transition-all duration-300 hover:scale-105 shadow-lg mt-6 md:mt-8 mb-3 md:mb-4"
  >
    Add to Cart - ${!isNaN(totalPrice) ? totalPrice.toFixed(2) : product.price}
  </button>
)}

          </div>
        </div>

        {/* Modals stay the same size */}
        {showColorError && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full shadow-2xl">
              <div className="text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <svg className="w-6 h-6 md:w-8 md:h-8 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Please Select a Colour</h3>
                <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">You need to choose a colour for your ribbon roses before adding to cart.</p>
                <button
                  onClick={() => setShowColorError(false)}
                  className="w-full bg-rose-600 hover:bg-rose-700 text-white py-2.5 md:py-3 rounded-lg text-base md:text-lg font-semibold transition-all duration-300"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        )}

        {showGiftMessageError && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl">
              <div className="text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <svg className="w-6 h-6 md:w-8 md:h-8 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Gift Message Required</h3>
                <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">Please include a heartfelt message for the recipient before adding to cart.</p>
                <button
                  onClick={() => setShowGiftMessageError(false)}
                  className="w-full bg-rose-600 hover:bg-rose-700 text-white py-2.5 md:py-3 rounded-lg md:rounded-xl text-base md:text-lg font-semibold transition-all duration-300"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        )}

        {showRamoSelectionError && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
    <div className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl">
      <div className="text-center">
        <div className="w-12 h-12 md:w-16 md:h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
          <svg className="w-6 h-6 md:w-8 md:h-8 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Rose Count Required</h3>
        <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">Please select the number of roses before adding to cart.</p>
        <button
          onClick={() => setShowRamoSelectionError(false)}
          className="w-full bg-rose-600 hover:bg-rose-700 text-white py-2.5 md:py-3 rounded-lg md:rounded-xl text-base md:text-lg font-semibold transition-all duration-300"
        >
          Got it!
        </button>
      </div>
    </div>
  </div>
)}

        {showColorPreferencesError && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl">
              <div className="text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <svg className="w-6 h-6 md:w-8 md:h-8 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Color Preferences Required</h3>
                <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">
                  Please either:
                  <br />
                  • Select at least one color tone, OR
                  <br />
                  • Describe your custom color preferences
                </p>
                <button
                  onClick={() => setShowColorPreferencesError(false)}
                  className="w-full bg-rose-600 hover:bg-rose-700 text-white py-2.5 md:py-3 rounded-lg md:rounded-xl text-base md:text-lg font-semibold transition-all duration-300"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        )}

      </div>

  {/* ✅ ADDED TO CART MODAL */}
{showAddedToCartModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl md:rounded-3xl max-w-md w-full p-6 md:p-8 shadow-2xl transform animate-scale-in">
      {/* Success Icon */}
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 md:w-20 md:h-20 bg-green-100 rounded-full flex items-center justify-center">
          <svg className="w-10 h-10 md:w-12 md:h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      </div>

      {/* Title */}
      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-3">
        Added to Cart!
      </h3>

      {/* Product Info */}
      <div className="flex items-center gap-4 bg-pink-50 rounded-xl p-4 mb-6">
        <img
          src={product.image}
          alt={product.name}
          className="w-16 h-16 md:w-20 md:h-20 object-cover rounded-lg"
        />
        <div className="flex-1">
          <p className="font-semibold text-gray-900">{product.name}</p>
          
          {/* Color Display */}
          {displayColor && (
            <p className="text-sm text-gray-600">
              {product.category === 'real' ? 'Colour Preference: ' : 'Colour: '}
              {displayColor}
            </p>
          )}

          {/* Rose Count (for ANY ramo style products, including Valentine's) */}
          {product.isRamoStyle && selectedRamoOption !== null && product.ramoOptions && (
            <p className="text-sm font-semibold text-rose-600">
              💐 {product.ramoOptions[selectedRamoOption].count} Roses
            </p>
          )}

          {/* Finish (for ribbon roses) */}
          {product.isCustomizable && selectedFinish !== 'Normal' && (
            <p className="text-sm text-gray-600">
              ✨ Finish: {selectedFinish}
            </p>
          )}

          {/* Corsage Type */}
          {product.category === 'corsage' && corsageType && (
            <p className="text-sm text-gray-600">
              Type: {corsageType === 'both' ? 'Corsage + Boutonniere' : corsageType.charAt(0).toUpperCase() + corsageType.slice(1)}
            </p>
          )}

          {/* Chocolate Type */}
{product.hasChocolateChoice && selectedChocolate && (
  <p className="text-sm text-gray-600">
    🍫 Chocolate: {selectedChocolate}
  </p>
)}

{product.isBundle && selectedBundle !== null && (
  <p className="text-sm text-gray-600">
    🎁 Bundle: {product.bundleOptions[selectedBundle].label}
  </p>
)}

          {/* Add-ons Display - Ribbon Roses */}
{product.isCustomizable && selectedAddOns.length > 0 && (
  <div className="text-sm text-gray-600 mt-1">
    <span className="font-medium">➕ Add-ons: </span>
    {selectedAddOns
      .filter(id => id !== 'ribbon-writing') // ✅ Exclude ribbon-writing from add-ons list
      .map(id => {
        const addon = addOnOptions.find(a => a.id === id);
        if (id === 'ferrero') {
          return `${addon.name} x${ferreroQuantity}`;
        }
        return addon.name;
      })
      .join(', ')}
  </div>
)}

          {/* Add-ons Display - Real Flowers & Valentine's */}
          {(product.category === 'real' || product.category === 'valentine') && selectedRealFlowerAddOns.length > 0 && (
            <div className="text-sm text-gray-600 mt-1">
              <span className="font-medium">➕ Add-ons: </span>
              {selectedRealFlowerAddOns.map(id => {
                let addon = realFlowerAddOns.find(a => a.id === id);
                if (!addon && product.addOns) {
                  addon = product.addOns.find(a => a.id === id);
                }
                return addon ? addon.name : '';
              }).filter(Boolean).join(', ')}
            </div>
          )}

          {/* Price */}
          <p className="text-rose-600 font-bold mt-1">${totalPrice.toFixed(2)}</p>
        </div>
      </div>

      {/* ✅ ADD: Card Message Display */}
{(wantCard && cardMessage) && (
  <div className="text-sm text-gray-600 mt-1 mb-4">
    <span className="font-medium">Card: </span>
    {cardMessage}
  </div>
)}

{product.category === 'mothersday' && selectedRealFlowerAddOns.includes('mday-card') && giftMessage && (
  <div className="text-sm text-gray-600 mt-1 mb-4">
    <span className="font-medium">Card Message: </span>
    {giftMessage}
  </div>
)}

{/* ✅ ADD: Ribbon Writing Display */}
{(wantRibbonWriting && ribbonMessage) && (
  <div className="text-sm text-gray-600 mt-1 mb-4">
    <span className="font-medium">Ribbon Writing: </span>
    {ribbonMessage}
  </div>
)}

{/* ✅ ADD: Gift Message Display (for Valentine's) */}
{(product.giftMessage?.required && giftMessage) && (
  <div className="text-sm text-gray-600 mt-1 mb-4">
    <span className="font-medium">Gift Message: </span>
    {giftMessage}
  </div>
)}

      {/* Action Buttons */}
      <div className="space-y-3">
        <button
          onClick={() => {
            setShowAddedToCartModal(false);
            navigate('/cart');
          }}
          className="w-full bg-rose-600 hover:bg-rose-700 text-white py-3 md:py-4 rounded-xl font-semibold transition text-sm md:text-base"
        >
          View Cart
        </button>
        <button
          onClick={() => setShowAddedToCartModal(false)}
          className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 md:py-4 rounded-xl font-semibold transition text-sm md:text-base"
        >
          Continue Shopping
        </button>
      </div>
    </div>
  </div>
)}

{showBundleError && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
    <div className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full shadow-2xl">
      <div className="text-center">
        <div className="w-12 h-12 md:w-16 md:h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
          <svg className="w-6 h-6 md:w-8 md:h-8 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Please Select a Bundle</h3>
        <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">
          Please choose your bundle combination before adding to cart.
        </p>
        <button
          onClick={() => setShowBundleError(false)}
          className="w-full bg-rose-600 hover:bg-rose-700 text-white py-2.5 md:py-3 rounded-lg text-base md:text-lg font-semibold transition-all duration-300"
        >
          Got it!
        </button>
      </div>
    </div>
  </div>
)}

{showBobaColorError && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
    <div className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full shadow-2xl">
      <div className="text-center">
        <div className="w-12 h-12 md:w-16 md:h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
          <svg className="w-6 h-6 md:w-8 md:h-8 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Please Select Balloon Colour</h3>
        <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">You need to choose a balloon colour combo before adding to cart.</p>
        <button
          onClick={() => setShowBobaColorError(false)}
          className="w-full bg-rose-600 hover:bg-rose-700 text-white py-2.5 md:py-3 rounded-lg text-base md:text-lg font-semibold transition-all duration-300"
        >
          Got it!
        </button>
      </div>
    </div>
  </div>
)}

{showChocolateError && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
    <div className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full shadow-2xl">
      <div className="text-center">
        <div className="w-12 h-12 md:w-16 md:h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
          <svg className="w-6 h-6 md:w-8 md:h-8 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Please Select Chocolate Type</h3>
        <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">You need to choose between Ferrero Rocher or Rafaello before adding to cart.</p>
        <button
          onClick={() => setShowChocolateError(false)}
          className="w-full bg-rose-600 hover:bg-rose-700 text-white py-2.5 md:py-3 rounded-lg text-base md:text-lg font-semibold transition-all duration-300"
        >
          Got it!
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default ProductDetail;